import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import {
  Spinner,
  Row,
  Col,
  Table,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Badge,
} from "reactstrap";
import {
  getAllLLMModels,
  createLLMModel,
  updateLLMModel,
  deleteLLMModel,
} from "../ApiCalls/llmModels";
import PageHeader from "./components/PageHeader";

const select = (state) => {
  return {
    token: state.auth.token,
  };
};

const LLMModels = (props) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingModelId, setEditingModelId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    modelId: "",
    displayName: "",
    provider: "OPENAI",
    description: "",
    metadata: {},
  });

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllLLMModels(props.token);
      if (response.success) {
        setModels(response.data || []);
        setError(null);
      } else {
        setError("Failed to fetch LLM models");
      }
    } catch (err) {
      console.error("Error fetching models:", err);
      setError("Error fetching LLM models");
    } finally {
      setLoading(false);
    }
  }, [props.token]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleOpenModal = (model = null) => {
    if (model) {
      setIsEditing(true);
      setEditingModelId(model._id);
      setFormData({
        modelId: model.modelId,
        displayName: model.displayName,
        provider: model.provider,
        description: model.description || "",
        metadata: model.metadata || {},
      });
    } else {
      setIsEditing(false);
      setEditingModelId(null);
      setFormData({
        modelId: "",
        displayName: "",
        provider: "OPENAI",
        description: "",
        metadata: {},
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingModelId(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      if (!formData.modelId.trim() || !formData.displayName.trim()) {
        setError("Model ID and Display Name are required");
        return;
      }

      setSubmitting(true);
      let response;

      if (isEditing) {
        response = await updateLLMModel(props.token, editingModelId, formData);
      } else {
        response = await createLLMModel(props.token, formData);
      }

      if (response.success) {
        setError(null);
        handleCloseModal();
        await fetchModels();
      } else {
        setError(response.message || "Failed to save model");
      }
    } catch (err) {
      console.error("Error saving model:", err);
      setError("Error saving LLM model");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (modelId) => {
    if (!window.confirm("Are you sure you want to delete this model?")) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await deleteLLMModel(props.token, modelId);

      if (response.success) {
        setError(null);
        await fetchModels();
      } else {
        setError(response.message || "Failed to delete model");
      }
    } catch (err) {
      console.error("Error deleting model:", err);
      setError("Error deleting LLM model");
    } finally {
      setSubmitting(false);
    }
  };

  const providerBadgeColor = (provider) => {
    switch (provider) {
      case "OPENAI":
        return "success";
      case "ANTHROPIC":
        return "primary";
      case "GOOGLE":
        return "info";
      default:
        return "secondary";
    }
  };

  if (loading) {
    return (
      <div className="text-center" style={{ padding: "2rem" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="LLMs"
        subtitle="Configuración de los modelos de lenguaje disponibles"
      />

      <div style={{ padding: "0 1rem 2rem" }}>
        {error && <Alert color="danger">{error}</Alert>}

        <Row className="mb-3">
          <Col>
            <Button
              color="primary"
              onClick={() => handleOpenModal()}
              disabled={submitting}
            >
              + Add New Model
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {models.length === 0 ? (
              <p>No LLM models configured yet.</p>
            ) : (
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>Model ID</th>
                    <th>Display Name</th>
                    <th>Provider</th>
                    <th>Description</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={model._id}>
                      <td>
                        <code>{model.modelId}</code>
                      </td>
                      <td>{model.displayName}</td>
                      <td>
                        <Badge color={providerBadgeColor(model.provider)}>
                          {model.provider}
                        </Badge>
                      </td>
                      <td>{model.description || "-"}</td>
                      <td>
                        {model.isActive ? (
                          <Badge color="success">Active</Badge>
                        ) : (
                          <Badge color="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          color="warning"
                          onClick={() => handleOpenModal(model)}
                          disabled={submitting}
                          style={{ marginRight: "0.5rem" }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() => handleDelete(model._id)}
                          disabled={submitting}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        <Modal isOpen={showModal} toggle={handleCloseModal}>
          <ModalHeader toggle={handleCloseModal}>
            {isEditing ? "Edit LLM Model" : "Add New LLM Model"}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="modelId">Model ID *</Label>
                <Input
                  type="text"
                  name="modelId"
                  id="modelId"
                  placeholder="e.g., gpt-4, claude-opus-4-8"
                  value={formData.modelId}
                  onChange={handleFormChange}
                  disabled={isEditing}
                />
              </FormGroup>

              <FormGroup>
                <Label for="displayName">Display Name *</Label>
                <Input
                  type="text"
                  name="displayName"
                  id="displayName"
                  placeholder="e.g., GPT-4 Turbo"
                  value={formData.displayName}
                  onChange={handleFormChange}
                />
              </FormGroup>

              <FormGroup>
                <Label for="provider">Provider *</Label>
                <Input
                  type="select"
                  name="provider"
                  id="provider"
                  value={formData.provider}
                  onChange={handleFormChange}
                >
                  <option value="OPENAI">OpenAI</option>
                  <option value="ANTHROPIC">Anthropic</option>
                  <option value="GOOGLE">Google</option>
                  <option value="OTHER">Other</option>
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  id="description"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={handleFormChange}
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSave} disabled={submitting}>
              {submitting ? <Spinner size="sm" color="white" /> : "Save"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default connect(select)(LLMModels);
